import { pvpRosterSize } from '@kenocyber/shared';
import {
  COMBAT_TEXT_LIFE_MS,
  COMBAT_TEXT_REFLECT_GAP_PX,
  COMBAT_TEXT_REFLECT_LABEL,
  COMBAT_TEXT_REFLECT_LABEL_PX,
  COMBAT_TEXT_REFLECT_LABEL_STYLE,
  COMBAT_TEXT_REFLECT_VALUE_STYLE,
  COMBAT_TEXT_RISE_PX,
  COMBAT_TEXT_SPREAD_STEPS,
  combatTextSpread,
  formatCombatNumber,
} from './combat-text';

/**
 * **`REFLECT` e o valor, como UMA composição** (gate V28G.O).
 *
 * ## Porque um contentor, e porque não dois textos soltos
 *
 * O contrato é explícito: *«uma composição visual, não dois textos que possam
 * separar-se»*. Duas etiquetas independentes seriam duas coisas com dois
 * tweens, e bastava um deles falhar - um `onComplete` que não corre, um pool que
 * recicla um e não o outro - para o jogador ver um `REFLECT` sozinho a subir
 * sobre uma nave, ou um número sem etiqueta.
 *
 * Um contentor resolve isso por CONSTRUÇÃO. As duas metades são filhas dele, em
 * coordenadas locais fixas, e nada neste ficheiro lhes toca depois de as
 * posicionar: o que se move, apaga e recicla é sempre o contentor inteiro. Elas
 * não podem separar-se porque não há código que as separe.
 *
 * ## Porque não um `Text` de duas linhas
 *
 * Porque as duas metades têm tamanhos diferentes - a etiqueta é 60 % do número -
 * e um objeto `Text` tem um estilo só. Fazê-las do mesmo tamanho cumpriria a
 * letra da regra da unidade e quebraria a do *«`REFLECT` pequeno»*.
 *
 * ## O que este módulo NÃO sabe
 *
 * O que é uma joia, um perfil, um socket, um nível ou uma fórmula. As entradas
 * são um ponto, um número inteiro de dano e um índice de leque. O valor é
 * formatado pelo formatador de combate que já existe - o mesmo que a caça e o
 * duelo usam - e não por uma segunda regra de formatação escrita aqui.
 */

/* -------------------------------------------------------------------------- */
/*  O teto, derivado                                                          */
/* -------------------------------------------------------------------------- */

/** Quantos cascos uma sala pode ter. Lido, nunca escrito. */
const HULLS = pvpRosterSize('team_3v3');

/**
 * **Quantos tiros um casco pode ter no ar ao mesmo tempo: 15.**
 *
 * `tempo de vida / intervalo` do duelo, a mesma conta que a derivação do pool do
 * Ciclone faz. Escrito aqui como um número porque este módulo é de
 * APRESENTAÇÃO e não pode importar o balanceamento do duelo sem passar a saber o
 * que é uma nave; `reflect-number.test.ts` afirma a igualdade contra as
 * constantes reais, que é onde conhecer os dois lados é legítimo.
 */
const SHOTS_IN_FLIGHT = 15;

/**
 * **Quantas composições podem coexistir: 90.**
 *
 * DERIVADO do pior caso que as regras permitem, e nunca escolhido por parecer
 * suficiente: `6 cascos x 15 tiros no ar` são os contactos que uma sala pode
 * resolver dentro de uma vida de texto, e uma corrida com a afordância de
 * desenvolvimento ligada pode intercetar todos eles.
 *
 * ## O que isto NÃO é
 *
 * Não é uma reserva. O pool cresce por procura: um duelo comum tem uma ou duas
 * composições vivas e aloca duas ou quatro. O teto só limita o crescimento,
 * portanto subi-lo não custa memória a quem nunca lá chega.
 *
 * E é um limite sobre um DEFEITO, como o do Ciclone: chegar-lhe significa que
 * alguma coisa saiu do que esta conta previu, e a resposta certa é refazer a
 * conta - nunca descartar um número em silêncio. Um número que a autoridade
 * confirmou não pode desaparecer por um limite de desenho.
 */
export const REFLECT_NUMBER_POOL = HULLS * SHOTS_IN_FLIGHT;

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface ReflectNumberText {
  x: number;
  y: number;
  setText(value: string): ReflectNumberText;
  setOrigin(x: number, y?: number): ReflectNumberText;
  destroy(): void;
}

export interface ReflectNumberContainer {
  x: number;
  y: number;
  alpha: number;
  setDepth(value: number): ReflectNumberContainer;
  setVisible(value: boolean): ReflectNumberContainer;
  destroy(): void;
}

export interface ReflectNumberScene {
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  add: {
    text(x: number, y: number, content: string, style: unknown): ReflectNumberText;
    container(x: number, y: number, children?: unknown[]): ReflectNumberContainer;
  };
}

/** Um número a imprimir: onde, quanto, e em que posição do leque. */
export interface ReflectNumber {
  readonly x: number;
  readonly y: number;
  /** O dano REALMENTE aplicado, em pontos inteiros. Autoritativo. */
  readonly amount: number;
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

interface Live {
  readonly box: ReflectNumberContainer;
  readonly label: ReflectNumberText;
  readonly value: ReflectNumberText;
  readonly tweens: { remove(): void }[];
  active: boolean;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * O leque é o MESMO que os números de dano usam - `combatTextSpread`, uma
 * sequência determinística de seis posições - e o índice vive aqui, na cena,
 * como vive lá. Dois números que nasçam no mesmo alvo no mesmo tique não se
 * empilham num borrão.
 */
export class ReflectNumberSystem {
  private readonly pool: Live[] = [];
  private spread = 0;
  private next = 0;
  private disposed = false;

  constructor(
    private readonly scene: ReflectNumberScene,
    private readonly depth: number,
    /**
     * Quanto acima do centro do casco a composição nasce, em píxeis.
     *
     * Decidido pela CENA e não aqui: ela é a única que sabe o tamanho desenhado
     * de um casco, e o contrato pede que o número *«não cubra barra ou casco»* -
     * uma distância fixa escrita neste módulo cobriria um casco grande.
     */
    private readonly offsetPx: number,
  ) {}

  /** Imprime `REFLECT` e o valor sobre um alvo. */
  print(number: ReflectNumber): void {
    if (this.disposed || number.amount <= 0) {
      /*
       * Zero não imprime, e é a mesma regra do dano comum: um golpe que não
       * chegou a um ponto inteiro não tem número, e imprimir zero seria imprimir
       * um número que não é o dano de nada.
       */
      return;
    }
    const live = this.take();
    live.active = true;
    live.value.setText(formatCombatNumber(number.amount));

    const fan = combatTextSpread(this.spread % COMBAT_TEXT_SPREAD_STEPS);
    this.spread += 1;

    const startX = number.x + fan.x;
    const startY = number.y - this.offsetPx + fan.y;
    live.box.x = startX;
    live.box.y = startY;
    live.box.alpha = 1;
    live.box.setVisible(true);

    /*
     * **O contentor inteiro sobe e apaga.** Uma composição, um tween.
     *
     * As duas metades não são alvos: elas estão em coordenadas locais e não se
     * movem uma em relação à outra. É isto que faz "não podem separar-se" ser
     * uma propriedade da estrutura em vez de uma promessa.
     */
    this.push(live, {
      targets: live.box,
      y: startY - COMBAT_TEXT_RISE_PX,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => this.release(live),
    });
  }

  /** Quantas composições estão vivas agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.pool.filter((entry) => entry.active).length;
  }

  /** Quantos objetos o sistema alocou ao todo. */
  pooledCount(): number {
    return this.pool.length;
  }

  /** Apaga tudo o que está no ecrã, sem largar os objetos. */
  clear(): void {
    for (const live of this.pool) {
      this.release(live);
    }
  }

  /**
   * Larga tudo. Os tweens ANTES dos objetos: um tween cujo alvo foi destruído
   * continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.pool) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      /*
       * O CONTENTOR, e só ele: destruí-lo destrói as filhas. Destruir as filhas
       * primeiro deixaria o contentor a apontar para objetos mortos durante a
       * mesma passagem.
       */
      live.box.destroy();
    }
    this.pool.length = 0;
  }

  private push(live: Live, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private release(live: Live): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.box.setVisible(false);
  }

  /**
   * Um slot do pool: reaproveitado se houver um livre, criado se não houver.
   *
   * Ao teto - e só lá - o mais antigo é reciclado, pela mesma escolha que o
   * Ciclone faz e pela mesma razão: entre calar o número NOVO e abreviar o mais
   * VELHO, abrevia-se o velho. Ele já viveu a maior parte dos seus 580 ms, e o
   * novo é o golpe que o jogador está a ver acontecer.
   */
  private take(): Live {
    const free = this.pool.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    if (this.pool.length >= REFLECT_NUMBER_POOL) {
      const oldest = this.pool[this.next % this.pool.length] as Live;
      this.next += 1;
      this.release(oldest);
      return oldest;
    }
    const created = this.build();
    this.pool.push(created);
    return created;
  }

  private build(): Live {
    /*
     * As duas metades, em coordenadas LOCAIS do contentor.
     *
     * A etiqueta com a origem no fundo-centro e o valor com a origem no
     * topo-centro, separados por {@link COMBAT_TEXT_REFLECT_GAP_PX}: é isso
     * *«imediatamente acima da numeração canónica»*, e é uma relação que fica
     * fixa no momento da construção em vez de ser recalculada a cada impressão.
     */
    const label = this.scene.add.text(
      0,
      -COMBAT_TEXT_REFLECT_GAP_PX,
      COMBAT_TEXT_REFLECT_LABEL,
      COMBAT_TEXT_REFLECT_LABEL_STYLE,
    );
    label.setOrigin(0.5, 1);
    const value = this.scene.add.text(0, 0, '0', COMBAT_TEXT_REFLECT_VALUE_STYLE);
    value.setOrigin(0.5, 0);
    const box = this.scene.add.container(0, 0, [label, value]);
    box.setDepth(this.depth).setVisible(false);
    return { box, label, value, tweens: [], active: false };
  }
}

/**
 * A altura total da composição, em píxeis. **Derivada**, para uma prova a medir.
 *
 * A etiqueta acima da linha de base do contentor, o intervalo, e o número
 * abaixo. Exportada porque a única maneira honesta de afirmar *«não cubra barra
 * ou casco»* é comparar esta altura com o deslocamento que a cena escolheu.
 */
export function reflectNumberHeightPx(valueFontPx: number): number {
  return COMBAT_TEXT_REFLECT_LABEL_PX + COMBAT_TEXT_REFLECT_GAP_PX + valueFontPx;
}
